import {
	SNOWPLOW_ENTITY,
	SNOWPLOW_ENVIRONMENT,
	SNOWPLOW_EVENT_TYPE,
	getSnowplowPlatformCode,
	getSnowplowPlacementDetails,
	snowplowEntityAttacher,
	snowplowEntityGenerator,
	snowplowEventGenerator,
	snowplowLog,
	snowplowPriceFormatter,
	snowplowProductsFormatter,
} from "@disco/disco_core";
import {
	BRAND_CLICK_TARGET,
	EVENT_TYPE,
	PRODUCT_CLICK_TARGET,
	WIDGET_CONSUMER_REDIRECT_TARGET,
} from "../event_conf";
import { ACTIVE_SNOWPLOW_ENV } from "../conf";
import message from "./message";
import { MESSAGE_TYPES } from "../hooks/useMessage";

export const SNOWPLOW_EVENT = {
	PAGE_LOAD: "PAGE_LOAD",
	WIDGET_LOAD: "WIDGET_LOAD",
	WIDGET_DISPLAY: "WIDGET_DISPLAY",
	WIDGET_VIEWABLE_THRESHOLD_DISPLAY: "WIDGET_VIEWABLE_THRESHOLD_DISPLAY",

	WIDGET_PRODUCT_DISPLAY: "WIDGET_PRODUCT_DISPLAY",
	WIDGET_PRODUCT_CLICK: "WIDGET_PRODUCT_CLICK",
	WIDGET_VIEWABLE_THRESHOLD_PRODUCT_DISPLAY:
		"WIDGET_VIEWABLE_THRESHOLD_PRODUCT_DISPLAY",

	WIDGET_BRAND_DISPLAY: "WIDGET_BRAND_DISPLAY",
	WIDGET_BRAND_CLICK: "WIDGET_BRAND_CLICK",
	WIDGET_VIEWABLE_THRESHOLD_BRAND_DISPLAY:
		"WIDGET_VIEWABLE_THRESHOLD_BRAND_DISPLAY",

	WIDGET_CONTACT_US_CLICK: "WIDGET_CONTACT_US_CLICK",
	WIDGET_CONSUMER_REDIRECT_CLICK: "WIDGET_CONSUMER_REDIRECT_CLICK",
	WIDGET_BRAND_ROW_OFFER_CLICK: "WIDGET_BRAND_ROW_OFFER_CLICK", // for the inline brand offer button
	WIDGET_LOAD_MORE_CLICK: "WIDGET_LOAD_MORE_CLICK",
	WIDGET_SHOW_ORDER_CLICK: "WIDGET_SHOW_ORDER_CLICK",
	WIDGET_TRACK_ORDER_CLICK: "WIDGET_TRACK_ORDER_CLICK",
	WIDGET_ELEMENT_CLICK: "WIDGET_ELEMENT_CLICK",

	WIDGET_ELEMENT_ENGAGEMENT: "WIDGET_ELEMENT_ENGAGEMENT",
};
export const IS_EXCLUSIVELY_SNOWPLOW_EVENT = new Set([
	SNOWPLOW_EVENT.WIDGET_VIEWABLE_THRESHOLD_DISPLAY,

	SNOWPLOW_EVENT.WIDGET_VIEWABLE_THRESHOLD_BRAND_DISPLAY,

	SNOWPLOW_EVENT.WIDGET_VIEWABLE_THRESHOLD_PRODUCT_DISPLAY,
	SNOWPLOW_EVENT.WIDGET_PRODUCT_DISPLAY,

	SNOWPLOW_EVENT.WIDGET_ELEMENT_ENGAGEMENT,
	SNOWPLOW_EVENT.WIDGET_LOAD,
	SNOWPLOW_EVENT.WIDGET_DISPLAY,
]);

const MIXPANEL_SNOWPLOW_EVENT_MAPPER = {
	[EVENT_TYPE.PAGE_LOAD]: SNOWPLOW_EVENT.PAGE_LOAD,
	[EVENT_TYPE.WIDGET_BRAND_DISPLAY]: SNOWPLOW_EVENT.WIDGET_BRAND_DISPLAY,
	[EVENT_TYPE.CLICK]: SNOWPLOW_EVENT.WIDGET_PRODUCT_CLICK,
	[EVENT_TYPE.CLICK_BRAND]: SNOWPLOW_EVENT.WIDGET_BRAND_CLICK,
	[EVENT_TYPE.WIDGET_ELEMENT_CLICK]: SNOWPLOW_EVENT.WIDGET_ELEMENT_CLICK,

	[EVENT_TYPE.WIDGET_CONTACT_US_CLICK]:
		SNOWPLOW_EVENT.WIDGET_CONTACT_US_CLICK,
	[EVENT_TYPE.WIDGET_COOP_LOGO_CLICK]:
		SNOWPLOW_EVENT.WIDGET_CONSUMER_REDIRECT_CLICK,
	[EVENT_TYPE.WIDGET_CONSUMER_REDIRECT_CLICK]:
		SNOWPLOW_EVENT.WIDGET_CONSUMER_REDIRECT_CLICK,
	[EVENT_TYPE.WIDGET_OFFER_CLICK]:
		SNOWPLOW_EVENT.WIDGET_BRAND_ROW_OFFER_CLICK,
	[EVENT_TYPE.WIDGET_SEE_MORE_CLICK]: SNOWPLOW_EVENT.WIDGET_LOAD_MORE_CLICK,
	[EVENT_TYPE.SHOW_ORDER_CLICK]: SNOWPLOW_EVENT.WIDGET_SHOW_ORDER_CLICK,
	[EVENT_TYPE.WIDGET_TRACK_ORDER_CLICK]:
		SNOWPLOW_EVENT.WIDGET_TRACK_ORDER_CLICK,
};

const SNOWPLOW_EVENT_SCHEMA_VERSION = {
	[SNOWPLOW_EVENT.PAGE_LOAD]: {
		[SNOWPLOW_ENVIRONMENT.DEVELOPMENT]: "1-0-0",
		[SNOWPLOW_ENVIRONMENT.PRODUCTION]: "1-0-0",
	},
	[SNOWPLOW_EVENT.WIDGET_LOAD]: {
		[SNOWPLOW_ENVIRONMENT.DEVELOPMENT]: "1-0-0",
		[SNOWPLOW_ENVIRONMENT.PRODUCTION]: "1-0-0",
	},
	[SNOWPLOW_EVENT.WIDGET_DISPLAY]: {
		[SNOWPLOW_ENVIRONMENT.DEVELOPMENT]: "1-0-0",
		[SNOWPLOW_ENVIRONMENT.PRODUCTION]: "1-0-0",
	},
	[SNOWPLOW_EVENT.WIDGET_VIEWABLE_THRESHOLD_DISPLAY]: {
		[SNOWPLOW_ENVIRONMENT.DEVELOPMENT]: "1-0-0",
		[SNOWPLOW_ENVIRONMENT.PRODUCTION]: "1-0-0",
	},

	[SNOWPLOW_EVENT.WIDGET_PRODUCT_DISPLAY]: {
		[SNOWPLOW_ENVIRONMENT.DEVELOPMENT]: "1-0-4",
		[SNOWPLOW_ENVIRONMENT.PRODUCTION]: "1-0-4",
	},
	[SNOWPLOW_EVENT.WIDGET_PRODUCT_CLICK]: {
		[SNOWPLOW_ENVIRONMENT.DEVELOPMENT]: "1-0-4",
		[SNOWPLOW_ENVIRONMENT.PRODUCTION]: "1-0-4",
	},
	[SNOWPLOW_EVENT.WIDGET_VIEWABLE_THRESHOLD_PRODUCT_DISPLAY]: {
		[SNOWPLOW_ENVIRONMENT.DEVELOPMENT]: "1-0-2",
		[SNOWPLOW_ENVIRONMENT.PRODUCTION]: "1-0-2",
	},

	[SNOWPLOW_EVENT.WIDGET_BRAND_DISPLAY]: {
		[SNOWPLOW_ENVIRONMENT.DEVELOPMENT]: "1-0-2",
		[SNOWPLOW_ENVIRONMENT.PRODUCTION]: "1-0-2",
	},
	[SNOWPLOW_EVENT.WIDGET_BRAND_CLICK]: {
		[SNOWPLOW_ENVIRONMENT.DEVELOPMENT]: "1-0-4",
		[SNOWPLOW_ENVIRONMENT.PRODUCTION]: "1-0-4",
	},
	[SNOWPLOW_EVENT.WIDGET_VIEWABLE_THRESHOLD_BRAND_DISPLAY]: {
		[SNOWPLOW_ENVIRONMENT.DEVELOPMENT]: "1-0-2",
		[SNOWPLOW_ENVIRONMENT.PRODUCTION]: "1-0-2",
	},

	[SNOWPLOW_EVENT.WIDGET_CONTACT_US_CLICK]: {
		[SNOWPLOW_ENVIRONMENT.DEVELOPMENT]: "1-0-1",
		[SNOWPLOW_ENVIRONMENT.PRODUCTION]: "1-0-1",
	},
	[SNOWPLOW_EVENT.WIDGET_CONSUMER_REDIRECT_CLICK]: {
		[SNOWPLOW_ENVIRONMENT.DEVELOPMENT]: "1-0-0",
		[SNOWPLOW_ENVIRONMENT.PRODUCTION]: "1-0-0",
	},
	[SNOWPLOW_EVENT.WIDGET_BRAND_ROW_OFFER_CLICK]: {
		[SNOWPLOW_ENVIRONMENT.DEVELOPMENT]: "1-0-0",
		[SNOWPLOW_ENVIRONMENT.PRODUCTION]: "1-0-0",
	},
	[SNOWPLOW_EVENT.WIDGET_LOAD_MORE_CLICK]: {
		[SNOWPLOW_ENVIRONMENT.DEVELOPMENT]: "1-0-0",
		[SNOWPLOW_ENVIRONMENT.PRODUCTION]: "1-0-0",
	},
	[SNOWPLOW_EVENT.WIDGET_SHOW_ORDER_CLICK]: {
		[SNOWPLOW_ENVIRONMENT.DEVELOPMENT]: "1-0-0",
		[SNOWPLOW_ENVIRONMENT.PRODUCTION]: "1-0-0",
	},
	[SNOWPLOW_EVENT.WIDGET_TRACK_ORDER_CLICK]: {
		[SNOWPLOW_ENVIRONMENT.DEVELOPMENT]: "1-0-1",
		[SNOWPLOW_ENVIRONMENT.PRODUCTION]: "1-0-1",
	},
	[SNOWPLOW_EVENT.WIDGET_ELEMENT_CLICK]: {
		[SNOWPLOW_ENVIRONMENT.DEVELOPMENT]: "1-3-0",
		[SNOWPLOW_ENVIRONMENT.PRODUCTION]: "1-3-0",
	},
	[SNOWPLOW_EVENT.WIDGET_ELEMENT_ENGAGEMENT]: {
		[SNOWPLOW_ENVIRONMENT.DEVELOPMENT]: "1-0-0",
		[SNOWPLOW_ENVIRONMENT.PRODUCTION]: "1-0-0",
	},
};

const SNOWPLOW_EVENT_SCHEMA = ({ version = "" } = {}) => ({
	[SNOWPLOW_EVENT.PAGE_LOAD]: `iglu:com.disconetwork/page_load/jsonschema/${version}`,
	[SNOWPLOW_EVENT.WIDGET_LOAD]: `iglu:com.disconetwork/widget_load/jsonschema/${version}`,
	[SNOWPLOW_EVENT.WIDGET_DISPLAY]: `iglu:com.disconetwork/widget_display/jsonschema/${version}`,
	[SNOWPLOW_EVENT.WIDGET_VIEWABLE_THRESHOLD_DISPLAY]: `iglu:com.disconetwork/widget_viewable_threshold_display/jsonschema/${version}`,

	[SNOWPLOW_EVENT.WIDGET_PRODUCT_DISPLAY]: `iglu:com.disconetwork/widget_product_display/jsonschema/${version}`,
	[SNOWPLOW_EVENT.WIDGET_PRODUCT_CLICK]: `iglu:com.disconetwork/widget_product_click/jsonschema/${version}`,
	[SNOWPLOW_EVENT.WIDGET_VIEWABLE_THRESHOLD_PRODUCT_DISPLAY]: `iglu:com.disconetwork/widget_viewable_threshold_product_display/jsonschema/${version}`,

	[SNOWPLOW_EVENT.WIDGET_BRAND_DISPLAY]: `iglu:com.disconetwork/widget_brand_display/jsonschema/${version}`,
	[SNOWPLOW_EVENT.WIDGET_BRAND_CLICK]: `iglu:com.disconetwork/widget_brand_click/jsonschema/${version}`,
	[SNOWPLOW_EVENT.WIDGET_VIEWABLE_THRESHOLD_BRAND_DISPLAY]: `iglu:com.disconetwork/widget_viewable_threshold_brand_display/jsonschema/${version}`,

	[SNOWPLOW_EVENT.WIDGET_CONTACT_US_CLICK]: `iglu:com.disconetwork/widget_contact_us_click/jsonschema/${version}`,
	[SNOWPLOW_EVENT.WIDGET_CONSUMER_REDIRECT_CLICK]: `iglu:com.disconetwork/widget_consumer_redirect_click/jsonschema/${version}`,
	[SNOWPLOW_EVENT.WIDGET_BRAND_ROW_OFFER_CLICK]: `iglu:com.disconetwork/widget_brand_row_offer_click/jsonschema/${version}`,
	[SNOWPLOW_EVENT.WIDGET_LOAD_MORE_CLICK]: `iglu:com.disconetwork/widget_load_more_click/jsonschema/${version}`,
	[SNOWPLOW_EVENT.WIDGET_SHOW_ORDER_CLICK]: `iglu:com.disconetwork/widget_show_order_click/jsonschema/${version}`,
	[SNOWPLOW_EVENT.WIDGET_TRACK_ORDER_CLICK]: `iglu:com.disconetwork/widget_track_order_click/jsonschema/${version}`,

	[SNOWPLOW_EVENT.WIDGET_ELEMENT_CLICK]: `iglu:com.disconetwork/widget_element_click/jsonschema/${version}`,
	[SNOWPLOW_EVENT.WIDGET_ELEMENT_ENGAGEMENT]: `iglu:com.disconetwork/widget_element_engagement/jsonschema/${version}`,
});

export const SNOWPLOW_EVENT_DATA_KEY = "_sp_event_data";
export const SNOWPLOW_SCHEMA_DATA_KEY = "_sp_schema_data";

const getAttachedSnowplowEntities = ({
	snowplowEventIdx,
	baseProperties,
	eventProperties,
	environment,
}) => {
	let attachedEntities = [];
	const purchaseEntity = snowplowEntityGenerator({
		entity: SNOWPLOW_ENTITY.PURCHASE,
		properties: eventProperties,
		propertyMapper: {
			products: "purchases",
		},
		additionalProperties: {
			products: snowplowProductsFormatter({
				products: eventProperties?.purchases,
			}),
		},
		environment,
	});
	const orderEntity = snowplowEntityGenerator({
		entity: SNOWPLOW_ENTITY.ORDER,
		properties: baseProperties,
		propertyMapper: {
			orderId: "order_id",
			scrapedCost: "cost",
		},
		additionalProperties: {
			remoteId: baseProperties?.shopify_order_id
				? String(baseProperties.shopify_order_id)
				: undefined,
			cost: snowplowPriceFormatter({
				price: baseProperties?.cost,
			}),
		},
		environment,
	});

	const hasPlatformEntity = !!getSnowplowPlatformCode({
		properties: baseProperties,
	});

	const platformEntity = snowplowEntityGenerator({
		entity: SNOWPLOW_ENTITY.PLATFORM,
		properties: baseProperties,
		propertyMapper: {
			shopUrl: "shopify_shop",
		},
		additionalProperties: {
			code: getSnowplowPlatformCode({ properties: baseProperties }),
		},
		environment,
	});
	const hasBrandEntity = !!eventProperties?.brand;
	const brandEntity = snowplowEntityGenerator({
		entity: SNOWPLOW_ENTITY.BRAND,
		properties: eventProperties,
		propertyMapper: {
			id: "brand_id",
			name: "brand",
		},
		environment,
	});
	const productEntity = snowplowEntityGenerator({
		entity: SNOWPLOW_ENTITY.PRODUCT,
		properties: eventProperties,
		propertyMapper: {
			id: "product_id",
			name: "product",
		},
		environment,
	});
	const hasNurtureOfferEntity =
		eventProperties?.[SNOWPLOW_SCHEMA_DATA_KEY]?.incentiveRemoteId;
	const nurtureOfferEntity = snowplowEntityGenerator({
		entity: SNOWPLOW_ENTITY.NURTURE_OFFER,
		properties: eventProperties?.[SNOWPLOW_SCHEMA_DATA_KEY],
		propertyMapper: {
			heading: "incentiveHeading",
			code: "incentiveCode",
			subheading: "incentiveSubheading",
			offerType: "incentiveType",
			remoteId: "incentiveRemoteId",
		},
	});

	const nativeSnowplowEventData =
		eventProperties?.[SNOWPLOW_EVENT_DATA_KEY] || {};

	const nativeSnowplowSchemaData =
		eventProperties?.[SNOWPLOW_SCHEMA_DATA_KEY] || {};

	// Core Generic Events
	switch (snowplowEventIdx) {
		case SNOWPLOW_EVENT.PAGE_LOAD:
		case SNOWPLOW_EVENT.WIDGET_LOAD:
		case SNOWPLOW_EVENT.WIDGET_DISPLAY:
		case SNOWPLOW_EVENT.WIDGET_VIEWABLE_THRESHOLD_DISPLAY: {
			attachedEntities = [purchaseEntity, orderEntity];
			attachedEntities = snowplowEntityAttacher({
				entities: attachedEntities,
				additionalEntity: platformEntity,
				canAttachEntity: !!hasPlatformEntity,
			});
			break;
		}
		case SNOWPLOW_EVENT.WIDGET_PRODUCT_DISPLAY:
		case SNOWPLOW_EVENT.WIDGET_VIEWABLE_THRESHOLD_PRODUCT_DISPLAY:
		case SNOWPLOW_EVENT.WIDGET_PRODUCT_CLICK: {
			const displayPositionEntity = snowplowEntityGenerator({
				entity: SNOWPLOW_ENTITY.DISPLAY_POSITION,
				properties: eventProperties,
				propertyMapper: {
					verticalPosition: "_brand_view_order",
					horizontalPosition: "product_view_order",
				},
				environment,
			});
			attachedEntities = [
				productEntity,
				brandEntity,
				displayPositionEntity,
			];
			break;
		}

		case SNOWPLOW_EVENT.WIDGET_BRAND_DISPLAY:
		case SNOWPLOW_EVENT.WIDGET_VIEWABLE_THRESHOLD_BRAND_DISPLAY:
		case SNOWPLOW_EVENT.WIDGET_BRAND_CLICK:
		case SNOWPLOW_EVENT.WIDGET_BRAND_ROW_OFFER_CLICK: {
			const displayPositionEntity = snowplowEntityGenerator({
				entity: SNOWPLOW_ENTITY.DISPLAY_POSITION,
				properties: eventProperties,
				propertyMapper: {
					verticalPosition: "brand_view_order",
				},
				environment,
			});
			attachedEntities = [brandEntity, displayPositionEntity];
			attachedEntities = snowplowEntityAttacher({
				entities: attachedEntities,
				additionalEntity: nurtureOfferEntity,
				canAttachEntity: !!hasNurtureOfferEntity,
			});
			break;
		}

		case SNOWPLOW_EVENT.WIDGET_SHOW_ORDER_CLICK:
		case SNOWPLOW_EVENT.WIDGET_TRACK_ORDER_CLICK: {
			attachedEntities = [orderEntity];
			attachedEntities = snowplowEntityAttacher({
				entities: attachedEntities,
				additionalEntity: platformEntity,
				canAttachEntity: !!hasPlatformEntity,
			});
			break;
		}

		case SNOWPLOW_EVENT.WIDGET_ELEMENT_CLICK: {
			const { horizontalPosition } = nativeSnowplowSchemaData;

			// TODO: Think about generalizing this check in the long term once fully migrated to snowplow
			if (horizontalPosition !== undefined) {
				const displayPositionEntity = snowplowEntityGenerator({
					entity: SNOWPLOW_ENTITY.DISPLAY_POSITION,
					properties: nativeSnowplowSchemaData,
				});
				attachedEntities = [displayPositionEntity];
			}
			attachedEntities = snowplowEntityAttacher({
				entities: attachedEntities,
				additionalEntity: brandEntity,
				canAttachEntity: !!hasBrandEntity,
			});
			attachedEntities = snowplowEntityAttacher({
				entities: attachedEntities,
				additionalEntity: nurtureOfferEntity,
				canAttachEntity: !!hasNurtureOfferEntity,
			});
			break;
		}

		case SNOWPLOW_EVENT.WIDGET_ELEMENT_ENGAGEMENT: {
			const { initiatingTarget = "" } = nativeSnowplowEventData;

			// Note: Can also filter / attach properties by engagementType, currently done by initiatingTarget
			// const { initiatingTarget = "", engagementType = "" } = nativeSnowplowEventData;
			switch (initiatingTarget) {
				case BRAND_CLICK_TARGET.DISCO_FEED_1: {
					const displayPositionEntity = snowplowEntityGenerator({
						entity: SNOWPLOW_ENTITY.DISPLAY_POSITION,
						properties: eventProperties,
						propertyMapper: {
							verticalPosition: "brand_view_order",
						},
						environment,
					});
					attachedEntities = [brandEntity, displayPositionEntity];
				}
			}
		}
	}

	return attachedEntities;
};

const snowplowEventFormatter = ({
	inputEventName,
	baseProperties,
	eventProperties,
	environment = SNOWPLOW_ENVIRONMENT.PRODUCTION,
	allProperties,
}) => {
	if (!inputEventName) {
		snowplowLog("snowplowEventFormatter >>", "Event name not found");
		return null;
	}

	let type = SNOWPLOW_EVENT_TYPE.SELF_DESCRIBING;

	switch (inputEventName) {
		case EVENT_TYPE.WIDGET_SHOP_INSTA_CLICK:
		case EVENT_TYPE.WIDGET_SHOP_TWITTER_CLICK:
		case EVENT_TYPE.WIDGET_SHOP_FB_CLICK:
			type = SNOWPLOW_EVENT_TYPE.SOCIAL_INTERACTION;
			break;
	}

	if (
		!IS_EXCLUSIVELY_SNOWPLOW_EVENT.has(inputEventName) &&
		type === SNOWPLOW_EVENT_TYPE.SELF_DESCRIBING &&
		!(inputEventName in MIXPANEL_SNOWPLOW_EVENT_MAPPER)
	) {
		// 1. Check if event is not exclusively snowplow
		// 2.1 Check if event is self describing (due to exception being SOCIAL events)
		// 2.2 && does not have corresponding mapping from Mixpanel => Snowplow
		snowplowLog(
			"snowplowEventFormatter >>",
			"Mixpanel <> Snowplow mapping not found"
		);
		return null;
	}

	let event = {};
	const globalEntities = [
		snowplowEntityGenerator({
			entity: SNOWPLOW_ENTITY.PUBLISHER,
			properties: baseProperties,
			propertyMapper: { id: "publisher", name: "publisher_name" },
			environment,
		}),
		snowplowEntityGenerator({
			entity: SNOWPLOW_ENTITY.SESSION_CONFIGURATION,
			properties: allProperties,
			propertyMapper: {
				totalModules: "total_modules",
				crossSellAlgorithmType: "cross_sell_algorithm_type",
				crossSellAlgorithmVersion: "cross_sell_algorithm_version",
				widgetVersion: "widget_version",
				widgetType: "widget_type",
				widgetMode: "widget_mode",
				mlModel: "ml_model",
				cacheSize: "cache_size",
				recommenderMechanism: "recommender_mechanism",
				runningExperiments: "running_experiments", // Ensure running_experiments is configured according to snowplow standards
			},
			additionalProperties: {
				placementDetails: getSnowplowPlacementDetails({
					properties: baseProperties,
				}),
			},
			environment,
		}),
		snowplowEntityGenerator({
			entity: SNOWPLOW_ENTITY.DISCO_DIMENSIONS,
			properties: baseProperties,
			propertyMapper: {
				windowWidth: "device_width",
				windowHeight: "device_height",
				frameWidth: "frame_width",
				frameHeight: "frame_height",
			},
			environment,
		}),
		snowplowEntityGenerator({
			entity: SNOWPLOW_ENTITY.PROMOTED_IDENTIFICATION,
			properties: allProperties,
			propertyMapper: {
				userId: "recommendation_id",
				insertionId: "insertion_id",
				isAnonymous: "is_anon_recommendation",
			},
		}),
		snowplowEntityGenerator({
			entity: SNOWPLOW_ENTITY.KODDI_METADATA,
			properties: allProperties,
			propertyMapper: {
				trackingData: "tracking_data",
				beaconIssued: "beacon_issued",
			},
		}),
	];

	switch (type) {
		case SNOWPLOW_EVENT_TYPE.SOCIAL_INTERACTION: {
			let network = "";
			switch (inputEventName) {
				case EVENT_TYPE.WIDGET_SHOP_INSTA_CLICK: {
					network = "instagram";
					break;
				}
				case EVENT_TYPE.WIDGET_SHOP_TWITTER_CLICK: {
					network = "twitter";
					break;
				}
				case EVENT_TYPE.WIDGET_SHOP_FB_CLICK: {
					network = "facebook";
					break;
				}
			}
			event = {
				action: "click",
				network,
				target: eventProperties?.link,
			};
			break;
		}
		case SNOWPLOW_EVENT_TYPE.SELF_DESCRIBING: {
			const snowplowEventIdx = IS_EXCLUSIVELY_SNOWPLOW_EVENT.has(
				inputEventName
			)
				? inputEventName
				: MIXPANEL_SNOWPLOW_EVENT_MAPPER[inputEventName];

			const schemaVersion =
				SNOWPLOW_EVENT_SCHEMA_VERSION[snowplowEventIdx][environment];
			const schema = SNOWPLOW_EVENT_SCHEMA({ version: schemaVersion })[
				snowplowEventIdx
			];

			let attachedEntities = getAttachedSnowplowEntities({
				snowplowEventIdx,
				baseProperties,
				eventProperties,
				environment,
			});

			let eventData = {
				...(eventProperties?.[SNOWPLOW_EVENT_DATA_KEY] || {}),
			};
			switch (snowplowEventIdx) {
				case SNOWPLOW_EVENT.WIDGET_CONSUMER_REDIRECT_CLICK: {
					eventData = {
						initiatingTarget:
							eventProperties.target ||
							WIDGET_CONSUMER_REDIRECT_TARGET.DISCO_LOGO,
					};
					break;
				}
				case SNOWPLOW_EVENT.WIDGET_VIEWABLE_THRESHOLD_PRODUCT_DISPLAY:
				case SNOWPLOW_EVENT.WIDGET_PRODUCT_DISPLAY: {
					eventData = {
						price: snowplowPriceFormatter({
							price: eventProperties?.price,
						}),
						discountedPrice: snowplowPriceFormatter({
							price: eventProperties?.discounted_price,
						}),
						initiatingTarget: eventProperties.initiatingTarget,
					};
					break;
				}
				case SNOWPLOW_EVENT.WIDGET_PRODUCT_CLICK: {
					eventData = {
						targetUrl: !eventProperties?.isInternalClick
							? eventProperties?.product_redirect_link
							: undefined, // only pass targetUrl if event is NOT an internal click
						initiatingTarget:
							eventProperties?.initiatingTarget ||
							eventProperties?.target ||
							PRODUCT_CLICK_TARGET.DISCO_FEED_1,
					};
					break;
				}
				case SNOWPLOW_EVENT.WIDGET_VIEWABLE_THRESHOLD_BRAND_DISPLAY:
				case SNOWPLOW_EVENT.WIDGET_BRAND_DISPLAY: {
					eventData = {
						initiatingTarget:
							eventProperties?.initiatingTarget ||
							eventProperties?.target,
					};
					break;
				}
				case SNOWPLOW_EVENT.WIDGET_BRAND_CLICK: {
					eventData = {
						targetUrl: !eventProperties?.isInternalClick
							? eventProperties?.brand_redirect_link
							: undefined, // only pass targetUrl if event is NOT an internal click
						initiatingTarget:
							eventProperties?.initiatingTarget ||
							eventProperties?.target,
					};
					break;
				}
				case SNOWPLOW_EVENT.WIDGET_CONTACT_US_CLICK: {
					eventData = {
						contactEmail: baseProperties?.contact_email,
						targetUrl: eventProperties?.link,
					};
					break;
				}
				case SNOWPLOW_EVENT.WIDGET_TRACK_ORDER_CLICK: {
					eventData = {
						targetUrl: eventProperties?.link,
					};
					break;
				}
				case SNOWPLOW_EVENT.WIDGET_ELEMENT_CLICK: {
					eventData = {
						initiatingTarget: eventProperties.initiatingTarget,
					};
					break;
				}
			}

			event = snowplowEventGenerator({
				schema,
				data: eventData,
				entities: [...globalEntities, ...attachedEntities],
			});
		}
	}

	return { type, event };
};

export const eventRouter = ({
	name,
	baseProperties,
	eventProperties,
	allProperties,
} = {}) => {
	const snowplowData = snowplowEventFormatter({
		environment: ACTIVE_SNOWPLOW_ENV,
		inputEventName: name,
		baseProperties,
		eventProperties,
		allProperties,
	});

	let trackPayload = { snowplow: snowplowData };

	if (!IS_EXCLUSIVELY_SNOWPLOW_EVENT.has(name)) {
		trackPayload = { ...trackPayload, name, properties: allProperties };
	}

	message(MESSAGE_TYPES.TRACK, trackPayload);
};

export default snowplowEventFormatter;
